import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Scrollbars } from '@onsolve/onsolve-ui-components';

import { localeService } from 'services';
import { isCRC168Enabled } from 'featureFlags';

const LanguageList = ({ currentLanguage, onLanguageChange, listLabel }) => {
    const languageList = localeService.locales;
    const listRef = useRef(null);
    const [focusedLanguage, setFocusedLanguage] = useState(null);

    const handleKeyDown = (event, language) => {
        const isArrowDown = event.key === 'ArrowDown' || event.keyCode === 40;
        const isArrowUp = event.key === 'ArrowUp' || event.keyCode === 38;
        const isSelectKey = event.key === 'Enter' || event.key === ' ' || event.keyCode === 13 || event.keyCode === 32;

        // reactstrap's Dropdown prevents default on Enter/Space, which suppresses the native button click
        if (isSelectKey) {
            event.preventDefault();
            event.stopPropagation();
            onLanguageChange(event, language);

            return;
        }

        if (!isArrowDown && !isArrowUp) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        const buttons = Array.from(listRef.current.querySelectorAll('button.onsolve-lang'));
        const currentIndex = buttons.indexOf(event.currentTarget);
        const nextIndex = (currentIndex + (isArrowDown ? 1 : -1) + buttons.length) % buttons.length;
        const nextButton = buttons[nextIndex];

        nextButton.focus();
        if (nextButton.scrollIntoView) {
            nextButton.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        }
    };

    return (
        <div
            className={classNames('onsolve-underline-text onsolve-language-dropdown-scroll', {
                'crc168-accessibility': isCRC168Enabled(),
            })}
        >
            <Scrollbars vertical>
                <div
                    className="onsolve-language-container"
                    key={languageList.length}
                    ref={listRef}
                    role={isCRC168Enabled() ? 'listbox' : undefined}
                    aria-label={isCRC168Enabled() ? listLabel : undefined}
                    tabIndex={isCRC168Enabled() ? 0 : undefined}
                    onKeyDown={
                        isCRC168Enabled()
                            ? (event) => {
                                  if (event.target !== event.currentTarget) {
                                      return;
                                  }
                                  const isArrowUp = event.key === 'ArrowUp' || event.keyCode === 38;
                                  const isArrowDown = event.key === 'ArrowDown' || event.keyCode === 40;

                                  if (!isArrowUp && !isArrowDown) {
                                      return;
                                  }
                                  event.preventDefault();
                                  event.stopPropagation();
                                  const buttons = listRef.current.querySelectorAll('button.onsolve-lang');
                                  const button = buttons[isArrowUp ? buttons.length - 1 : 0];

                                  if (button) {
                                      button.focus();
                                  }
                              }
                            : undefined
                    }
                >
                    {languageList.map((language) => {
                        const isSelected = currentLanguage.lcId === language.lcId;
                        const handleEvent = (e) => onLanguageChange(e, language);

                        return (
                            <div
                                className={classNames('onsolve-active-list', {
                                    'onsolve-active-list--selected': isSelected,
                                    'onsolve-active-list--focused':
                                        isCRC168Enabled() && focusedLanguage === language.lcId,
                                    'onsolve-rtl-lang-item': language.direction === 'RTL',
                                })}
                                key={language.lcId}
                                onClick={isCRC168Enabled() ? undefined : handleEvent}
                                onKeyDown={isCRC168Enabled() ? undefined : handleEvent}
                            >
                                <button
                                    type="button"
                                    href="#"
                                    className="onsolve-lang btn btn-link p-0 border-0"
                                    role={isCRC168Enabled() ? 'option' : undefined}
                                    tabIndex={isCRC168Enabled() ? -1 : undefined}
                                    aria-label={
                                        isCRC168Enabled()
                                            ? `${language.translatedName || ''} ${
                                                  language.cultureRegionCode || ''
                                              }`.trim() || language.cultureRegionCode
                                            : language.translatedName
                                    }
                                    aria-selected={isCRC168Enabled() ? isSelected : undefined}
                                    title={language.translatedName}
                                    onFocus={isCRC168Enabled() ? () => setFocusedLanguage(language.lcId) : undefined}
                                    onBlur={isCRC168Enabled() ? () => setFocusedLanguage(null) : undefined}
                                    onClick={isCRC168Enabled() ? handleEvent : undefined}
                                    onKeyDown={isCRC168Enabled() ? (e) => handleKeyDown(e, language) : undefined}
                                >
                                    <span>{language.translatedName}</span>
                                    <span className="text-uppercase">({language.cultureRegionCode})</span>
                                </button>
                            </div>
                        );
                    })}
                </div>
            </Scrollbars>
        </div>
    );
};

LanguageList.propTypes = {
    currentLanguage: PropTypes.shape({
        lcId: PropTypes.string.isRequired,
    }).isRequired,
    onLanguageChange: PropTypes.func.isRequired,
    listLabel: PropTypes.string,
};

LanguageList.defaultProps = {
    changeLanguage: () => {},
    onLanguageChange: () => {},
};

export default LanguageList;
